import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import compareAsc from 'date-fns/compareAsc';
import { ClientModel, useClientContext, useClientTokenManager } from '@cko/dashboard-shared';

import settings from '../../../../../services/app-settings';
import { useFetchNotifications } from '../../hooks/useFetchNotifications';
import { useUpdateReadStatus } from '../../hooks/useUpdateReadStatus';
import { APIResponseToNotification } from '../../utils/mapper';

import { APIResponse, Notification } from '../../types';
import { lastSeenStorage } from '../../utils/timestamp';

type NotificationsContextValue = {
  canFetchMore: boolean;
  fetchInitialNotifications: () => Promise<void>;
  fetchNextBatchFromId: (notificationId: string, createdTimestamp: string) => Promise<void>;
  hasErrorLoadingList: boolean;
  hasUnseenNotifications: boolean;
  isLoadingList: boolean;
  notifications: Notification[];
  updateLastSeenTimestamp: () => void;
  updateNotificationReadStatus: (notificationId: string, isRead: boolean) => Promise<void>;
};

export const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications() {
  const context = useContext(NotificationsContext);

  if (context === null) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }

  return context;
}

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [hasErrorLoadingList, setHasErrorLoadingList] = useState(false);
  const [canFetchMore, setCanFetchMore] = useState(true);
  const [lastSeenTimestamp, setLastSeenTimestamp] = useState(lastSeenStorage.getTimestamp());

  const { getClient } = useClientContext();
  const { accessToken } = useClientTokenManager();

  const fetchNotifications = useFetchNotifications();
  const fetchInitialNotifications = async () => {
    if (hasErrorLoadingList) {
      setHasErrorLoadingList(false);
    }

    try {
      setIsLoadingList(true);
      setCanFetchMore(true);

      const notificationsData = await fetchNotifications();
      const notificationsWithAccountNames = await enrichWithAccountNames(
        notificationsData.notifications,
      );
      setNotifications(notificationsWithAccountNames);

      setIsLoadingList(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching initial list of notifications', error);
      setIsLoadingList(false);
      setHasErrorLoadingList(true);
      setCanFetchMore(false);
    }
  };

  const fetchNextBatchFromId = async (notificationId: string, createdTimestamp: string) => {
    try {
      const notificationsData = await fetchNotifications(notificationId, createdTimestamp);
      const notificationsWithAccountNames = await enrichWithAccountNames(
        notificationsData.notifications,
      );

      if (notificationsWithAccountNames.length < 10) {
        setCanFetchMore(false);
      }

      setNotifications((previousNotifications) => [
        ...previousNotifications,
        ...notificationsWithAccountNames,
      ]);
    } catch (error) {
      throw new Error('Error fetching next batch of notifications');
    }
  };

  useWebSocket(settings.api.notificationsWssBasePath, {
    protocols: ['websocket', accessToken || ''],
    onMessage: async (message: MessageEvent<string>) => {
      try {
        const response: APIResponse = JSON.parse(message.data);
        const notification: Notification = APIResponseToNotification(response);
        if (!notification.eventId) {
          throw new Error('Incompatible notification data received via WebSocket');
        }
        if (
          notifications.some(
            (previousNotification) => notification.eventId === previousNotification.eventId,
          )
        ) {
          return;
        }

        const notificationWithAccountNames = (await enrichWithAccountNames([notification]))[0];
        notificationWithAccountNames.receivedTimestamp = new Date().toISOString();
        setNotifications((previousNotifications) => [
          notificationWithAccountNames,
          ...previousNotifications,
        ]);

        // Remove loading error
        setHasErrorLoadingList(false);
      } catch {
        // eslint-disable-next-line no-console
        console.error('Error parsing new notification received via WebSocket');
      }
    },
  });

  const updateReadStatus = useUpdateReadStatus();
  const updateNotificationReadStatus = async (eventId: string, isRead: boolean): Promise<void> => {
    try {
      await updateReadStatus(eventId, isRead);
      setNotifications((previousNotifications) =>
        previousNotifications.map((previousNotification) => {
          if (previousNotification.eventId === eventId) {
            return {
              ...previousNotification,
              isRead,
            };
          }
          return previousNotification;
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Could not update notification's read status", error);
    }
  };

  const enrichWithAccountNames = async (
    notificationsToEnrich: Notification[],
  ): Promise<Notification[]> => {
    const clientMap: Record<string, ClientModel> = {};
    const clientIds = new Set(notificationsToEnrich.map((notification) => notification.clientId));

    for (const clientId of clientIds.values()) {
      const client = await getClient(clientId);
      if (!client || !(client instanceof ClientModel)) {
        continue;
      }

      clientMap[client.id] = client;
    }

    return notificationsToEnrich.map((notification) => {
      return {
        ...notification,
        clientName: clientMap[notification.clientId].name || undefined,
        entityName: clientMap[notification.clientId].entities.find(
          (entity) => entity.id === notification.entityId,
        )?.name,
      };
    });
  };

  const updateLastSeenTimestamp = () => {
    const timeSeen = new Date().toISOString();
    setLastSeenTimestamp(timeSeen);
    lastSeenStorage.setTimestamp(timeSeen);
  };

  const getHasUnseenNotifications = (): boolean => {
    if (!notifications.length) {
      return false;
    } else if (!lastSeenTimestamp) {
      return true;
    }

    const lastSeenDate = new Date(lastSeenTimestamp);
    const mostRecentNotificationDate = new Date(notifications[0].receivedTimestamp);
    const isMostRecentNotificationAfterLastSeen =
      compareAsc(mostRecentNotificationDate, lastSeenDate) > 0;

    return isMostRecentNotificationAfterLastSeen;
  };
  const hasUnseenNotifications = getHasUnseenNotifications();

  useEffect(() => {
    fetchInitialNotifications();
  }, []);

  const value: NotificationsContextValue = useMemo(
    () => ({
      canFetchMore,
      fetchInitialNotifications,
      fetchNextBatchFromId,
      hasErrorLoadingList,
      hasUnseenNotifications,
      isLoadingList,
      notifications,
      updateLastSeenTimestamp,
      updateNotificationReadStatus,
    }),
    [
      canFetchMore,
      fetchInitialNotifications,
      fetchNextBatchFromId,
      hasErrorLoadingList,
      hasUnseenNotifications,
      isLoadingList,
      notifications,
      updateLastSeenTimestamp,
      updateNotificationReadStatus,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};
